<template>
  <div>
    <b-tabs class="mb-1">
      <b-tab :title="$t('cancellation.policies')" @click="$router.push('/cancellations/policies')" />
      <b-tab :title="$t('cancellation.product_rules')" @click="$router.push('/cancellations/product_rules')" />
      <b-tab :title="$t('cancellation.requests')" active />
    </b-tabs>

    <!-- Cards -->
    <div class="vx-row mb-2" v-if="stats">
      <div class="vx-col sm:w-1/4 w-full">
        <statistics-card-line
          hideChart
          icon="ZapIcon"
          icon-right
          :statistic="stats.auto_rate + '%'"
          :statisticTitle="$t('cancellation.stats_auto')"
          color="success"/>
      </div>
      <div class="vx-col sm:w-1/4 w-full">
        <statistics-card-line
          hideChart
          icon="ClockIcon"
          icon-right
          :statistic="stats.pending_count"
          :statisticTitle="$t('cancellation.stats_pending')"
          color="warning"/>
      </div>
      <div class="vx-col sm:w-1/4 w-full">
        <statistics-card-line
          hideChart
          icon="FlagIcon"
          icon-right
          :statistic="stats.contested_count"
          :statisticTitle="$t('cancellation.stats_contested')"
          color="danger"/>
      </div>
      <div class="vx-col sm:w-1/4 w-full">
        <statistics-card-line
          hideChart
          icon="RepeatIcon"
          icon-right
          :statistic="stats.reschedule_rate + '%'"
          :statisticTitle="$t('cancellation.stats_reschedule')"
          color="primary"/>
      </div>
    </div>

    <div class="flex justify-between items-center flex-wrap mb-1">
      <b-tabs v-model="tabIndex" @input="changeTab">
        <b-tab :title="$t('cancellation.tab_pending') + (stats ? ' · ' + stats.pending_count : '')" />
        <b-tab :title="$t('cancellation.tab_auto')" />
        <b-tab :title="$t('cancellation.tab_contested')" />
        <b-tab :title="$t('cancellation.tab_all')" />
      </b-tabs>
      <b-button
        v-permission="'cancellation_requests.edit'"
        variant="outline-danger"
        @click="$refs.clubCancelModal.open()">
        <feather-icon icon="XCircleIcon" class="mr-50"/>
        <span class="align-middle">{{ $t('cancellation.club_cancel') }}</span>
      </b-button>
    </div>

    <Grid
      ref="grid"
      :service="service"
      :hide_actions="true"
      :forceFilters="tabFilters"
      route_name="cancellation_requests"
      order_column="created_at"
      direction="asc">
      <template v-slot:gridThead>
        <vs-th key="protocol" sort-key="protocol">{{ $t('cancellation.protocol') }}</vs-th>
        <vs-th key="customer" sort-key="customer_name">{{ $t('cancellation.student') }}</vs-th>
        <vs-th key="product">{{ $t('cancellation.product') }}</vs-th>
        <vs-th key="reason">{{ $t('cancellation.reason') }}</vs-th>
        <vs-th key="rule">{{ $t('cancellation.applied_rule') }}</vs-th>
        <vs-th key="remedy">{{ $t('cancellation.proposed_remedy') }}</vs-th>
        <vs-th key="age">{{ $t('cancellation.open_for') }}</vs-th>
        <vs-th key="actions">Ações</vs-th>
      </template>
      <template v-slot:gridData="props">
        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in props.data">
          <vs-td width="9%" key="protocol">
            <div>{{ tr.protocol }}</div>
          </vs-td>
          <vs-td width="13%" key="customer">
            <div>{{ tr.customer_name }}</div>
          </vs-td>
          <vs-td width="14%" key="product">
            <div>{{ tr.product_label }}</div>
          </vs-td>
          <!--
            Motivo médico é **dado pessoal sensível** (LGPD, Art. 5º, II). O
            relato clínico não é exibido na listagem: aparece só no detalhe, e
            cada abertura de detalhe é registrada em `privacy_access_logs`.
            Uma grade que imprime o estado de saúde de dezenas de alunos numa
            tela é exposição desnecessária — nenhum operador precisa disso para
            escolher qual pedido abrir.
          -->
          <vs-td width="12%" key="reason">
            <div v-if="tr.reason === 'ME'" class="health-reason-cell">
              <vs-chip transparent color="danger" class="text-base">Motivo médico</vs-chip>
              <small>Dado sensível — visível no detalhe</small>
            </div>
            <div v-else>{{ tr.reason_text || $t('cancellation.reason_.' + tr.reason) }}</div>
          </vs-td>
          <vs-td width="16%" key="rule">
            <div>{{ ruleLabel(tr) }}</div>
          </vs-td>
          <vs-td width="15%" key="remedy">
            <div>{{ remedyLabel(tr) }}</div>
          </vs-td>
          <vs-td width="8%" key="age">
            <vs-chip transparent :color="ageColor(tr.created_at)" class="text-base">
              {{ ageLabel(tr.created_at) }}
            </vs-chip>
          </vs-td>
          <vs-td width="13%" key="actions">
            <div class="flex flex-row gap-2" v-if="tr.status === 'PA'">
              <b-button
                v-permission="'cancellation_requests.edit'"
                variant="success" size="sm" class="mr-1"
                @click="approve(tr)">
                {{ $t('cancellation.approve') }}
              </b-button>
              <b-button
                v-permission="'cancellation_requests.edit'"
                variant="outline-danger" size="sm"
                @click="deny(tr)">
                {{ $t('cancellation.deny') }}
              </b-button>
            </div>
            <div v-else-if="tr.status === 'CO' || tr.status === 'HA'">
              <b-button
                v-permission="'cancellation_requests.edit'"
                variant="primary" size="sm"
                @click="$router.push(`/cancellations/requests/${tr.id}/contestation`)">
                {{ $t('cancellation.respond') }}
              </b-button>
            </div>
            <vs-chip v-else transparent :color="statusColor(tr.status)" class="text-base">
              {{ $t('cancellation.request_status_.' + tr.status) }}
            </vs-chip>
          </vs-td>
        </vs-tr>
      </template>
    </Grid>

    <ClubCancelModal ref="clubCancelModal" @executed="refresh"/>
  </div>
</template>

<script>
import { BButton, BTabs, BTab } from 'bootstrap-vue'
import moment from 'moment'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import { currencyFormatter } from '@/util/Util'

import CancellationRequestService from '@/services/api/CancellationRequestService'
import ClubCancelModal from './ClubCancelModal.vue'

export default {
  components: {
    BButton,
    BTabs,
    BTab,
    StatisticsCardLine,
    ClubCancelModal
  },
  data: () => ({
    service: null,
    stats: null,
    tabIndex: 0
  }),
  computed: {
    tabFilters() {
      switch (this.tabIndex) {
        case 0: return { status: 'PA' }
        case 1: return { resolution: 'AU' }
        case 2: return { status: 'CO,HA' }
        default: return {}
      }
    }
  },
  methods: {
    changeTab() {
      this.$nextTick(() => this.$refs.grid.fetchGridData(true))
    },
    refresh() {
      this.loadStats()
      this.$refs.grid.fetchGridData(true)
    },
    loadStats() {
      this.service.stats().then(stats => { this.stats = stats })
    },
    ruleLabel(tr) {
      const version = tr.policy_version ? `v${tr.policy_version.version}` : ''
      const tier = tr.tier ? this.tierRange(tr.tier) : ''
      const exception = tr.exception_key ? 'exceção' : ''
      return [version, tier, exception].filter(Boolean).join(' · ') || '—'
    },
    tierRange(tier) {
      if (tier.max_hours === null) return `mais de ${Number(tier.min_hours)}h`
      if (!tier.min_hours) return `menos de ${Number(tier.max_hours)}h`
      return `${Number(tier.min_hours)}h–${Number(tier.max_hours)}h`
    },
    remedyLabel(tr) {
      const name = this.$t('cancellation.remedy_.' + tr.remedy)
      if (tr.remedy === 'NR') return name
      return `${name} ${currencyFormatter(tr.remedy_amount)}`
    },
    ageLabel(createdAt) {
      const hours = moment().diff(moment(createdAt), 'hours')
      if (hours < 24) return `${hours} h`
      const days = Math.floor(hours / 24)
      const rest = hours % 24
      return rest ? `${days} d ${rest} h` : `${days} d`
    },
    ageColor(createdAt) {
      const hours = moment().diff(moment(createdAt), 'hours')
      if (hours < 24) return 'success'
      if (hours < 72) return 'warning'
      return 'danger'
    },
    statusColor(status) {
      return { AP: 'success', DE: 'danger', FI: 'secondary', CO: 'warning', HA: 'primary' }[status] || 'secondary'
    },
    approve(tr) {
      this.$vs.loading()
      this.service.approve(tr.id).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.refresh()
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    deny(tr) {
      this.$vs.loading()
      this.service.deny(tr.id).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.refresh()
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    }
  },
  beforeMount() {
    this.service = CancellationRequestService.build(this.$vs)
  },
  mounted() {
    this.loadStats()
  }
}
</script>

<style lang="scss" scoped>
/*
 * Motivo médico é dado de saúde (LGPD, Art. 11): a listagem mostra que existe,
 * não o que diz. O conteúdo fica no detalhe, cuja abertura é registrada.
 */
.health-reason-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  small {
    font-size: 0.68rem;
    color: #6e6b7b;
    line-height: 1.3;
  }
}
</style>
